import React, { useEffect, useState } from 'react'
import { Controller, useForm, useFieldArray } from 'react-hook-form'
import { DocumentIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { Button } from 'components/ui/button'
import InputField from 'components/styledFormElements/InputField'
import FileInputUploadField from 'components/styledFormElements/FileInputUploadField'
import { useUploadFiles } from 'hooks/upload'
import { getImageSrc, getRelativeIconPath } from 'services/util'
import { useAddSettings, useSettingsData, useUpdateSettings } from 'hooks/settings'
import { useNotificationBar } from 'context/notificationContext'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { X } from 'lucide-react'

interface ResourceSettingsFormData {
    heading: string
    subHeading: string
    primaryCtaText: string
    secondaryCtaText: string
    icon: string | null
}

interface EmptyStateResourceSlide {
    title: string
    description: string
    image: File | string | null
    arrowImage: File | string | null
}

interface AdditionalResourceSettingsFormData {
    slides: EmptyStateResourceSlide[]
}

const ResourceSettings = () => {
    const { mutate: addSettings, isLoading: addLoading } = useAddSettings()
    const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings()
    const { data: settingsData, refetch } = useSettingsData()
    const uploadMutation = useUploadFiles()
    const notification = useNotificationBar()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        getValues,
        formState: { errors },
        watch,
        control,
        reset,
        setError,
    } = useForm<ResourceSettingsFormData>({
        mode: 'onChange',
        defaultValues: {
            heading: '',
            subHeading: '',
            primaryCtaText: '',
            secondaryCtaText: '',
            icon: null,
        },
    })

    const {
        register: registerAdditional,
        handleSubmit: handleAdditionalSubmit,
        getValues: getAdditionalValues,
        setValue: setAdditionalValue,
        formState: { errors: additionalErrors },
        watch: watchAdditional,
        control: controlAdditional,
        reset: resetAdditional,
        setError: setAdditionalError,
        clearErrors: clearAdditionalErrors,
    } = useForm<AdditionalResourceSettingsFormData>({
        mode: 'onChange',
        defaultValues: {
            slides: [
                {
                    title: '',
                    description: '',
                    image: null,
                    arrowImage: null,
                }
            ],
        },
    })

    const { fields, append, remove } = useFieldArray({
        control: controlAdditional,
        name: 'slides',
    })

    const watchedIcon = watch('icon')

    const uploadImage = async (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('customPath', 'settings/images')

        const res = await uploadMutation.mutateAsync({
            data: formData,
        })

        return res?.data?.data?.key || ''
    }

    useEffect(() => {
        const hasExistingData =
            Array.isArray(settingsData?.data) &&
            settingsData.data.length > 0

        const existing = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === 'resource_setting')
            : null

        const additionalExisting = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === 'empty_state_resource_setting')
            : null

        if (existing?.settings) {
            reset({
                heading: existing.settings.heading || '',
                subHeading: existing.settings.subHeading || '',
                primaryCtaText: existing.settings.primaryCtaText || '',
                secondaryCtaText: existing.settings.secondaryCtaText || '',
                icon: existing.settings.icon || null,
            })
        }

        if (additionalExisting?.settings?.slides && Array.isArray(additionalExisting.settings.slides)) {
            resetAdditional({
                slides: additionalExisting.settings.slides
            })
        } else if (additionalExisting?.settings) {
            // Handle old single-item format
            resetAdditional({
                slides: [{
                    title: additionalExisting.settings.title || '',
                    description: additionalExisting.settings.description || '',
                    image: additionalExisting.settings.image || null,
                    arrowImage: additionalExisting.settings.arrowImage || null,
                }]
            })
        }
    }, [settingsData, reset, resetAdditional])

    const onSubmit = async (data: ResourceSettingsFormData) => {
        const hasExistingData = Array.isArray(settingsData?.data) && settingsData.data.length > 0
        const existingResourceSetting = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === 'resource_setting')
            : null

        // let logoImageKey: string | null = null

        // if (data.icon instanceof File) {
        //     try {
        //         logoImageKey = await uploadImage(data.icon)
        //     } catch (error) {
        //         setError('icon', {
        //             type: 'upload',
        //             message: 'Failed to upload widget icon'
        //         })
        //         return
        //     }
        // } else if (typeof data.icon === 'string') {
        //     // User didn't change icon → keep existing key
        //     logoImageKey = data.icon
        // }
        // Convert full URL → relative path (if needed)
        const iconPath = typeof data.icon === 'string' ? getRelativeIconPath(data.icon) : null

        const formData = {
            slug: "resource_setting",
            settings: {
                heading: data.heading,
                subHeading: data.subHeading,
                primaryCtaText: data.primaryCtaText,
                secondaryCtaText: data.secondaryCtaText,
                icon: iconPath || null,
            },
            status: 1,
            is_delete: 0
        }

        if (existingResourceSetting?.id) {
            updateSettings(
                {
                    data: formData,
                    setting_id: existingResourceSetting.id,
                },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Resource settings updated successfully',
                        })
                        refetch()
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to update resource settings',
                        })
                    },
                }
            )
        } else {
            addSettings(
                { data: formData },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Resource settings created successfully',
                        })
                        refetch()
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to create resource settings',
                        })
                    },
                }
            )
        }
    }

    const onAdditionalSubmit = async (data: AdditionalResourceSettingsFormData) => {
        setIsSubmitting(true)
        // Validate all items
        let hasErrors = false
        data.slides.forEach((slide, index) => {
            if (!slide.title) {
                setAdditionalError(`slides.${index}.title` as any, {
                    type: 'required',
                    message: 'Title is required'
                })
                hasErrors = true
            }
            if (!slide.description) {
                setAdditionalError(`slides.${index}.description` as any, {
                    type: 'required',
                    message: 'Description is required'
                })
                hasErrors = true
            }
        })

        if (hasErrors) {
            setIsSubmitting(false)
            return
        }

        const hasExistingData = Array.isArray(settingsData?.data) && settingsData.data.length > 0
        const existingAdditionalSetting = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === 'empty_state_resource_setting')
            : null

        // Process all items - upload files and build the items array
        const processedItems: Array<{
            title: string
            description: string
            image: string | null
            arrowImage: string | null
        }> = []
        for (let i = 0; i < data.slides.length; i++) {
            const slide = data.slides[i]
            let imagePath: string | null = null
            let arrowImagePath: string | null = null

            // Handle image upload
            if (slide.image instanceof File) {
                try {
                    imagePath = await uploadImage(slide.image)
                } catch (error) {
                    setAdditionalError(`slides.${i}.image` as any, {
                        type: 'upload',
                        message: 'Failed to upload image'
                    })
                    setIsSubmitting(false)
                    return
                }
            } else if (typeof slide.image === 'string') {
                imagePath = getRelativeIconPath(slide.image)
            }

            // Handle arrow image upload
            if (slide.arrowImage instanceof File) {
                try {
                    arrowImagePath = await uploadImage(slide.arrowImage)
                } catch (error) {
                    setAdditionalError(`slides.${i}.arrowImage` as any, {
                        type: 'upload',
                        message: 'Failed to upload arrow image'
                    })
                    setIsSubmitting(false)
                    return
                }
            } else if (typeof slide.arrowImage === 'string') {
                arrowImagePath = getRelativeIconPath(slide.arrowImage)
            }

            processedItems.push({
                title: slide.title,
                description: slide.description,
                image: imagePath || null,
                arrowImage: arrowImagePath || null,
            })
        }

        const formData = {
            slug: "empty_state_resource_setting",
            settings: {
                slides: processedItems
            },
            status: 1,
            is_delete: 0
        }

        if (existingAdditionalSetting?.id) {
            updateSettings(
                {
                    data: formData,
                    setting_id: existingAdditionalSetting.id,
                },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Empty state resource settings updated successfully',
                        })
                        refetch()
                        setIsSubmitting(false)
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to update empty state resource settings',
                        })
                        setIsSubmitting(false)
                    },
                }
            )
        } else {
            addSettings(
                { data: formData },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Empty state resource settings created successfully',
                        })
                        refetch()
                        setIsSubmitting(false)
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to create empty state resource settings',
                        })
                        setIsSubmitting(false)
                    },
                }
            )
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
                    <aside className="flex flex-auto items-center gap-2">
                        <DocumentIcon className="h-6 w-6" />
                        <h4 className="text-xl">Resource Settings</h4>
                    </aside>

                    <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                        <div className="space-y-6">
                            {/* First Row: Heading, Primary CTA Text, Secondary CTA Text */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Heading Field */}
                                <div>
                                    <InputField
                                        label="Heading"
                                        type="text"
                                        required
                                        helpText="This text will appear as the main title of the Resource section on the show page (Resource, Show Materials)."
                                        placeholder="Enter heading"
                                        register={register('heading', {
                                            required: 'Heading is required',
                                            maxLength: {
                                                value: 40,
                                                message: 'Heading must not exceed 40 characters',
                                            },
                                        })}
                                        error={errors?.heading?.message as string}
                                    />
                                </div>

                                {/* Primary CTA Text Field */}
                                <div>
                                    <InputField
                                        label="Primary CTA Text"
                                        type="text"
                                        required
                                        helpText="Label for the main action button. This button typically allows users to view or access the resource (View, Open, Access)."
                                        placeholder="Enter primary CTA text"
                                        register={register('primaryCtaText', {
                                            required: 'Primary CTA Text is required',
                                            maxLength: {
                                                value: 30,
                                                message: 'Primary CTA Text must not exceed 30 characters',
                                            },
                                        })}
                                        error={errors?.primaryCtaText?.message as string}
                                    />
                                </div>

                                {/* Secondary CTA Text Field */}
                                <div>
                                    <InputField
                                        label="Secondary CTA Text"
                                        type="text"
                                        required
                                        helpText="Label for the secondary action button. This is usually used for saving or bookmarking the resource (Save, Bookmark, Download)."
                                        placeholder="Enter secondary CTA text"
                                        register={register('secondaryCtaText', {
                                            required: 'Secondary CTA Text is required',
                                            maxLength: {
                                                value: 30,
                                                message: 'Secondary CTA Text must not exceed 30 characters',
                                            },
                                        })}
                                        error={errors?.secondaryCtaText?.message as string}
                                    />
                                </div>
                            </div>

                            {/* Second Row: Sub Heading and Icon Upload */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Sub Heading Field (Textarea) */}
                                <div>
                                    <InputField
                                        label="Sub Heading"
                                        type="text"
                                        required
                                        helpText="Short description displayed below the heading in the Resource section."
                                        isTextarea
                                        row={2}
                                        placeholder="Enter sub heading"
                                        register={register('subHeading', {
                                            required: 'Sub Heading is required',
                                            maxLength: {
                                                value: 250,
                                                message: 'Sub Heading must not exceed 250 characters',
                                            },
                                        })}
                                        error={errors?.subHeading?.message as string}
                                    />
                                </div>

                                {/* Icon Upload Field */}
                                <div>
                                    <div className="flex flex-col gap-2 w-full">
                                        {/* Label */}
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            Resource Icon <span className="text-red-700">*</span>
                                            <span className="relative group ml-1 inline-block">
                                                <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer inline" />
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                                                    Upload an SVG icon to represent the Resource section.
                                                </div>
                                            </span>
                                        </label>

                                        <Controller
                                            name="icon"
                                            control={control}
                                            render={({ field }) => {
                                                const iconValue = field.value;

                                                return (
                                                    <div className="relative">
                                                        {/* If icon selected → show inside field */}
                                                        {iconValue ? (
                                                            <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                                <div className="flex items-center gap-2">
                                                                    <img
                                                                        src={
                                                                            iconValue.startsWith('http')
                                                                                ? iconValue
                                                                                : getImageSrc(iconValue)
                                                                        }
                                                                        alt="Selected icon"
                                                                        className="w-6 h-6 object-contain"
                                                                    />
                                                                    <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                        Icon selected
                                                                    </span>
                                                                </div>

                                                                {/* Remove icon */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        field.onChange('');
                                                                    }}
                                                                    className="ml-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            /* Show dropdown when no icon selected */
                                                            <IconDropdown
                                                                value={iconValue}
                                                                onChange={(selectedIcon: any) => {
                                                                    const iconKey =
                                                                        selectedIcon?.path ||
                                                                        selectedIcon?.image ||
                                                                        selectedIcon?.icon ||
                                                                        '';

                                                                    field.onChange(iconKey);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            }}
                                        />

                                        {errors.icon && (
                                            <p className="text-xs text-red-600">{errors.icon.message}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-left my-6">
                        <Button color="blue" type="submit" isLoading={updateLoading || addLoading}>
                            Save Resource Settings
                        </Button>
                    </div>
                </div>
            </form>

            {/* Empty State Resource Settings Section */}
            <form onSubmit={handleAdditionalSubmit(onAdditionalSubmit)}>
                <div className="w-full px-6 pt-6 mt-8 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
                    <aside className="flex flex-auto items-center gap-2">
                        <DocumentIcon className="h-6 w-6" />
                        <h4 className="text-xl">Empty State Resource Settings</h4>
                    </aside>

                    {/* Dynamic Rows */}
                    {fields.map((field, index) => {
                        const watchedImage = watchAdditional(`slides.${index}.image` as any)
                        const watchedArrowImage = watchAdditional(`slides.${index}.arrowImage` as any)

                        return (
                            <div key={field.id} className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm relative">
                                {/* Remove Button - Show only if more than 1 row */}
                                {fields.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="absolute top-4 right-4 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                        title="Remove this row"
                                    >
                                        <X size={20} />
                                    </button>
                                )}

                                <h5 className="text-lg font-medium mb-4">Slide {index + 1}</h5>

                                <div className="space-y-6">
                                    {/* First Row: Title and Description */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Title Field */}
                                        <div>
                                            <InputField
                                                label="Title"
                                                type="text"
                                                required
                                                helpText="Enter the title for the empty state resource section."
                                                placeholder="Enter title"
                                                register={registerAdditional(`slides.${index}.title` as any, {
                                                    required: 'Title is required',
                                                    maxLength: {
                                                        value: 100,
                                                        message: 'Title must not exceed 100 characters',
                                                    },
                                                })}
                                                error={additionalErrors?.slides?.[index]?.title?.message as string}
                                            />
                                        </div>

                                        {/* Description Field (Textarea) */}
                                        <div>
                                            <InputField
                                                label="Description"
                                                type="text"
                                                required
                                                helpText="Enter the description for the empty state resource section. Note: Wrap text in ## to make it bold (e.g., ##bold text##)."
                                                isTextarea
                                                row={3}
                                                placeholder="Enter description"
                                                register={registerAdditional(`slides.${index}.description` as any, {
                                                    required: 'Description is required',
                                                    maxLength: {
                                                        value: 500,
                                                        message: 'Description must not exceed 500 characters',
                                                    },
                                                })}
                                                error={additionalErrors?.slides?.[index]?.description?.message as string}
                                            />
                                        </div>
                                    </div>

                                    {/* Second Row: Image and Arrow Image */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Image Field */}
                                        <div>
                                            <FileInputUploadField
                                                label="Image"
                                                fieldName={`slides.${index}.image`}
                                                register={registerAdditional(`slides.${index}.image` as any) as any}
                                                errors={{ [`slides.${index}.image`]: additionalErrors?.slides?.[index]?.image }}
                                                existingFile={typeof watchedImage === 'string' ? getImageSrc(watchedImage) : null}
                                                watchFile={watchedImage instanceof File ? watchedImage : null}
                                                onFileSelect={(file: File | null) => {
                                                    if (!file) {
                                                        setAdditionalValue(`slides.${index}.image` as any, null, { shouldValidate: true })
                                                        return
                                                    }
                                                    setAdditionalValue(`slides.${index}.image` as any, file, { shouldValidate: true })
                                                    clearAdditionalErrors(`slides.${index}.image` as any)
                                                }}
                                                tooltip={{
                                                    title: 'Image requirements',
                                                    items: [
                                                        'Upload an image for the empty state resource section',
                                                        'Formats: JPEG, PNG, GIF, WebP, SVG',
                                                        'File size: Up to 5 MB',
                                                    ],
                                                    position: 'top'
                                                }}
                                                allowedTypes={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']}
                                                containerClassName="w-full h-[120px] mb-4"
                                            />
                                        </div>

                                        {/* Arrow Image Field */}
                                        <div>
                                            <FileInputUploadField
                                                label="Arrow Image"
                                                fieldName={`slides.${index}.arrowImage`}
                                                register={registerAdditional(`slides.${index}.arrowImage` as any) as any}
                                                errors={{ [`slides.${index}.arrowImage`]: additionalErrors?.slides?.[index]?.arrowImage }}
                                                existingFile={typeof watchedArrowImage === 'string' ? getImageSrc(watchedArrowImage) : null}
                                                watchFile={watchedArrowImage instanceof File ? watchedArrowImage : null}
                                                onFileSelect={(file: File | null) => {
                                                    if (!file) {
                                                        setAdditionalValue(`slides.${index}.arrowImage` as any, null, { shouldValidate: true })
                                                        return
                                                    }
                                                    setAdditionalValue(`slides.${index}.arrowImage` as any, file, { shouldValidate: true })
                                                    clearAdditionalErrors(`slides.${index}.arrowImage` as any)
                                                }}
                                                tooltip={{
                                                    title: 'Arrow image requirements',
                                                    items: [
                                                        'Upload an arrow image for the empty state resource section',
                                                        'Formats: JPEG, PNG, GIF, WebP, SVG',
                                                        'File size: Up to 5 MB',
                                                    ],
                                                    position: 'top'
                                                }}
                                                allowedTypes={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']}
                                                containerClassName="w-full h-[120px] mb-4"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}

                    {/* Add More Button */}
                    <div className="my-4 px-6">
                        <Button
                            type="button"
                            color="gray"
                            onClick={() => append({
                                title: '',
                                description: '',
                                image: null,
                                arrowImage: null,
                            })}
                        >
                            + Add More Row
                        </Button>
                    </div>

                    <div className="flex justify-left my-6 px-6 pb-6">
                        <Button color="blue" type="submit" isLoading={isSubmitting || updateLoading || addLoading}>
                            Save Empty State Resource Settings
                        </Button>
                    </div>
                </div>
            </form>
        </>
    )
}

export default ResourceSettings